import { EntityDataDto, EntityDto } from "./indexDto";

export interface Entity {
	key: string,
	links: string[],
	depiction: Map<string, string>,
	alias: Map<string, string[]>,
	connections: Map<string, string[]>,
	tags: Map<string, string[]>,
}

export interface EntityData<T extends string | string[]> {
	[episodeId: string]: T
}

export class EntityCollection {
	private entitiesMap: Map<string, Entity> = new Map();

	get length(): number {
		return this.entitiesMap.size;
	}

	setSeries(series: string, entries: EntityDto[]) {
		for (const entry of entries) {
			const entity: Entity = {
				key: entry.key,
				links: entry.links || [],
				depiction: entityDataDtoToMap(entry.depiction, series),
				alias: entityDataDtoToMap(entry.alias, series),
				connections: entityDataDtoToMap(entry.connections, series),
				tags: entityDataDtoToMap(entry.tags, series)
			}
			this.set(entity);
		}
	}

	private set(entity: Entity): void {
		if (!entity || !entity.key) return;
		if (this.entitiesMap.has(entity.key)) {
			this.entitiesMap.set(entity.key, mergeEntities(this.entitiesMap.get(entity.key)!, entity));
			return;
		}
		this.entitiesMap.set(entity.key, entity);
	}

	find(key: string): Entity | undefined {
		return this.entitiesMap.get(key);
	}

	get(index: number): Entity | undefined {
		return Array.from(this.entitiesMap.values())[index];
	}

	forEach(callback: (entry: Entity) => void): void {
		this.entitiesMap.forEach(callback);
	}

	toArray(): Entity[] {
		return Array.from(this.entitiesMap.values());
	}

	sort(): void {
		this.entitiesMap = new Map([...this.entitiesMap.entries()].sort());
	}

	getAllTags(): string[] {
		const result = [...new Set(this.toArray().map(e =>
			Array.from(e.tags.values()).flat()
		).flat())];
		return result;
	}
}

export function episodeKey(seriesKey: string, episodeId: string | number) {
	return `${seriesKey}__${episodeId}`;
}

export function seriesKey(entityKey: string) {
	return entityKey.split('__')[0];
}

export function episodeId(entityKey: string) {
	return entityKey.split('__')[1];
}

function entityDataDtoToMap<T>(data: EntityDataDto<T> | undefined, seriesKey: string): Map<string, T> {
	return new Map<string, T>(Object.keys(data || {}).map(episodeId => [episodeKey(seriesKey, episodeId), data![episodeId]]));
}

function mergeEntities(entity1: Entity, entity2: Entity): Entity {
	return {
		key: entity1.key,
		links: [...new Set(entity1.links.concat(entity2.links))],
		depiction: new Map([...entity1.depiction, ...entity2.depiction]),
		alias: new Map([...entity1.alias, ...entity2.alias]),
		connections: new Map([...entity1.connections, ...entity2.connections]),
		tags: new Map([...entity1.tags, ...entity2.tags])
	}
}